import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Cookies from "js-cookie";
import { fetchUsage } from "../lib/billingApi";
import { useUpgradeModal } from "./UpgradeModal";

const LABELS = { responses: "form responses", ai_generations: "AI generations" };

// Global usage banner: amber at ≥80% of any metered resource, rose once a resource is
// exhausted. Consumes /api/usage. Dismissible per session; re-appears when the state
// escalates (warn → exceeded) or after a plan change.
export default function UsageWarningBanner() {
    const { openUpgradeModal } = useUpgradeModal();
    const [warnings, setWarnings] = useState([]);
    const [dismissedKey, setDismissedKey] = useState("");

    const load = async () => {
        if (!Cookies.get("token")) return;
        try {
            const data = await fetchUsage();
            setWarnings(data.warnings || []);
        } catch (_) { /* non-blocking */ }
    };

    useEffect(() => {
        load();
        const onChange = () => load();
        // A blocked action (e.g. form generation at 100%) opens the soft-block upgrade modal.
        const onLimit = () => { openUpgradeModal({ trigger: "usage_limit", dismissable: true }); load(); };
        window.addEventListener("cfai:plan-changed", onChange);
        window.addEventListener("cfai:usage-limit", onLimit);
        return () => {
            window.removeEventListener("cfai:plan-changed", onChange);
            window.removeEventListener("cfai:usage-limit", onLimit);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    if (!warnings.length) return null;

    const exceeded = warnings.filter((w) => w.exceeded);
    const isExceeded = exceeded.length > 0;
    // Distinct key per state so a warn→exceeded escalation re-shows a dismissed banner.
    const stateKey = warnings.map((w) => `${w.resource}:${w.exceeded ? "x" : "w"}`).sort().join("|");
    if (dismissedKey === stateKey) return null;

    const names = (isExceeded ? exceeded : warnings).map((w) => LABELS[w.resource] || w.resource);
    const list = names.length === 1 ? names[0] : `${names.slice(0, -1).join(", ")} and ${names[names.length - 1]}`;

    const tone = isExceeded
        ? "border-rose-300 dark:border-rose-700/60 bg-rose-50 dark:bg-rose-900/20 text-rose-800 dark:text-rose-200"
        : "border-amber-300 dark:border-amber-700/60 bg-amber-50 dark:bg-amber-900/20 text-amber-800 dark:text-amber-200";

    return (
        <div className={`border-b ${tone}`}>
            <div className="max-w-6xl mx-auto px-4 py-2.5 flex flex-wrap items-center justify-between gap-2">
                <p className="text-sm font-medium">
                    {isExceeded
                        ? `You've hit your monthly limit for ${list}. Upgrade or add a capacity boost to keep going.`
                        : `You're approaching your monthly limit for ${list}.`}
                </p>
                <div className="flex items-center gap-2">
                    <Link to="/settings/billing" className="text-xs font-semibold px-3 py-1.5 rounded-lg border border-current/30 hover:bg-black/5 dark:hover:bg-white/5 no-underline text-current">
                        {isExceeded ? "Add capacity" : "View usage"}
                    </Link>
                    <button
                        onClick={() => openUpgradeModal({ trigger: "usage_limit", dismissable: true })}
                        className="text-xs font-semibold px-3 py-1.5 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white"
                    >
                        Upgrade
                    </button>
                    <button onClick={() => setDismissedKey(stateKey)} aria-label="Dismiss" className="text-current/60 hover:text-current px-1 text-lg leading-none">×</button>
                </div>
            </div>
        </div>
    );
}
